import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Home, AlertTriangle } from "lucide-react";
import Layout from "../Layout";
import { Button } from "../../../components/ui/button";

function NotFound() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <Layout title={t("notFound.title")}>
      <section className="min-h-screen flex items-center justify-center px-4 py-12 md:py-16 bg-gradient-to-br from-muted via-background to-background relative overflow-hidden">
        {/* Background decorations */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl"></div>

        <div className="container mx-auto max-w-2xl relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-card rounded-2xl border-2 border-border shadow-xl p-8 md:p-12 text-center"
          >
            {/* 404 Icon */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2, type: "spring" }}
              className="mb-6 flex justify-center"
            >
              <div className="relative">
                <div className="absolute inset-0 bg-primary/20 rounded-full animate-ping"></div>
                <div className="relative bg-primary/10 rounded-full p-6">
                  <AlertTriangle className="w-20 h-20 md:w-24 md:h-24 text-primary" />
                </div>
              </div>
            </motion.div>

            {/* 404 Number */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-6xl md:text-8xl lg:text-9xl font-bold text-primary mb-4"
            >
              {t("notFound.title")}
            </motion.h1>

            {/* Heading */}
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-4"
            >
              {t("notFound.heading")}
            </motion.h2>

            {/* Message */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="text-lg md:text-xl text-muted-foreground mb-8"
            >
              {t("notFound.message")}
            </motion.p>

            {/* Back to Home Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <Button
                onClick={() => navigate("/")}
                className="bg-primary hover:bg-primary/90 text-onPrimary px-8 py-6 text-base md:text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2 mx-auto"
              >
                <Home className="w-5 h-5" />
                {t("notFound.backToHome")}
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}

export default NotFound;
